import { useApi } from "@/hooks/useApi";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useScrollToBottom } from "@/hooks/useScrollToBottom";
import { useSpeechInput } from "@/hooks/useSpeechInput";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CompanyDTO } from "@shaarp/common";
import { StarsIcon } from "lucide-react";
import { FormEvent, useCallback, useRef, useState } from "react";
import { RiStickyNoteLine } from "react-icons/ri";
import { SpeechInput, type SpeechInputHandle } from "../ai-elements/speech-input";
import VoiceRecordingBar from "../ai-elements/voice-recording-bar";
import { ButtonLoading } from "../button-loading";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Separator } from "../ui/separator";
import { MobileSheet } from "../ui/mobile-sheet";
import { SheetDescription, SheetTitle } from "../ui/sheet";
import { TextareaAutosize } from "../ui/textarea-autosize";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { useToast, useToastError } from "../ui/use-toast";

type CompanyNoteProps = {
  readonly company: CompanyDTO;
  readonly savedNote?: string;
  readonly onSavedNoteChange?: (note: string) => void;
  readonly onActionComplete?: () => void;
  readonly compact?: boolean;
  readonly className?: string;
};

export default function CompanyNote({
  company,
  savedNote: controlledSavedNote,
  onSavedNoteChange,
  onActionComplete,
  compact = false,
  className,
}: CompanyNoteProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const storedNote = company.note?.content || "";
  const [note, setNote] = useState(storedNote);
  const [internalSavedNote, setInternalSavedNote] = useState(storedNote);
  // A page whose company is replaced by a fresher copy (the run's snapshot, a
  // revisit) hands a newer note: it is taken without remounting, so an open
  // dialog stays open and the draft being written in it is kept.
  const [syncedNote, setSyncedNote] = useState(storedNote);
  if (syncedNote !== storedNote) {
    setSyncedNote(storedNote);
    setInternalSavedNote(storedNote);
    if (!isDialogOpen) setNote(storedNote);
  }
  const [isLoading, setIsLoading] = useState(false);

  const savedNote = controlledSavedNote ?? internalSavedNote;
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();
  const { isMobile } = useDeviceFormat();
  const {
    speechLanguage,
    handlePermissionDenied,
    handleError,
    handleConnectionLost,
  } = useSpeechInput();

  // Only one of the two textareas below is mounted at a time (mobile sheet or
  // desktop dialog), so a single ref covers both.
  const noteTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [isDictating, setIsDictating] = useState(false);
  const [isLiveTranscript, setIsLiveTranscript] = useState(true);
  const [recordingStream, setRecordingStream] = useState<MediaStream | null>(
    null,
  );
  useScrollToBottom(noteTextareaRef, note, isDictating);

  // Note content captured when dictation starts. Live transcription rewrites
  // the field on every delta, so it is rebuilt from this snapshot instead of
  // being appended to, which would duplicate text as the transcript grows.
  const noteSnapshotRef = useRef("");
  const speechInputRef = useRef<SpeechInputHandle>(null);
  // Which control ended the recording, so a discard can put the note back the
  // way it was. `onRecordingChange` alone cannot tell the two apart.
  const recordingActionRef = useRef<"none" | "cancel" | "confirm">("none");

  const handleRecordingChange = useCallback(
    (isRecording: boolean) => {
      setIsDictating(isRecording);
      if (isRecording) {
        noteSnapshotRef.current = note;
        return;
      }
      if (recordingActionRef.current === "cancel") {
        setNote(noteSnapshotRef.current);
      }
      recordingActionRef.current = "none";
    },
    [note],
  );

  const handleCancelRecording = useCallback(() => {
    recordingActionRef.current = "cancel";
    speechInputRef.current?.cancel();
  }, []);

  /**
   * Stop dictating but keep the text, so the note can be reviewed and edited
   * before it is saved rather than being submitted straight away.
   */
  const handleConfirmRecording = useCallback(() => {
    recordingActionRef.current = "confirm";
    speechInputRef.current?.stop();
    setTimeout(() => noteTextareaRef.current?.focus(), 0);
  }, []);

  const handleTranscript = useCallback((text: string) => {
    const snapshot = noteSnapshotRef.current;
    setNote(snapshot.trim() ? `${snapshot.trimEnd()} ${text}` : text);
  }, []);

  const updateNotes = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await api.put(`/company/${company.id}/note`, { note });
      setIsDialogOpen(false);
      toast({
        variant: "default",
        title: t("company.notes.updateTitle"),
        description: t("company.notes.updateSuccess"),
      });
      setInternalSavedNote(note);
      onSavedNoteChange?.(note);
      onActionComplete?.();
    } catch {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const lastModifiedLabel =
    company.note?.updatedAt && company.note?.lastUpdatedBy
      ? t("company.notes.lastModified", {
          date: new Date(company.note.updatedAt).toLocaleString(),
          name: company.note.lastUpdatedBy.fullName,
        })
      : null;

  const trigger = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "relative h-10 gap-2",
            compact ? "w-10" : "px-3",
            !!savedNote && "border-primary text-primary",
            className,
          )}
          onClick={() => setIsDialogOpen(true)}
        >
          <span className="relative shrink-0 inline-flex">
            <RiStickyNoteLine className="w-4 h-4" />
            {!!savedNote && (
              <span className="absolute -top-1 -right-1.5 w-2.5 h-2.5 rounded-full bg-current" />
            )}
          </span>
          {!compact && (
            <span className="text-sm">{t("company.notes.button")}</span>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {t("company.notes.button")}
      </TooltipContent>
    </Tooltip>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <MobileSheet open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <form
            onSubmit={updateNotes}
            className="flex-1 flex flex-col min-h-0"
          >
            <div className="px-5 pt-3 pb-4 shrink-0">
              <div className="flex items-start justify-between gap-3 pr-8">
                <SheetTitle className="text-2xl font-semibold tracking-tight">
                  {t("company.notes.title")}
                </SheetTitle>
              </div>
              {lastModifiedLabel && (
                <SheetDescription className="text-xs text-muted-foreground mt-1 line-clamp-1">
                  {lastModifiedLabel}
                </SheetDescription>
              )}

              <div className="mt-3 flex items-start gap-2 text-muted-foreground">
                <StarsIcon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary/70" />
                <p className="text-[12px] leading-snug">
                  <span className="font-medium text-foreground/80">
                    {t("company.notes.aiEnrichmentBadge")}
                  </span>
                  <span className="mx-1">·</span>
                  {t("company.notes.aiEnrichmentHint")}
                </p>
              </div>
            </div>

            <div className="flex-1 min-h-0 mx-5 mb-3 rounded-2xl bg-muted/40 ring-1 ring-border/60 overflow-hidden flex flex-col">
              <TextareaAutosize
                ref={noteTextareaRef}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("company.notes.placeholder")}
                className="flex-1 min-h-0 w-full resize-none bg-transparent border-0 shadow-none ring-0 focus-visible:ring-0 px-4 py-4 text-base leading-relaxed placeholder:text-muted-foreground/50 overflow-y-auto"
              />
            </div>

            <div className="shrink-0 border-t border-border/60 bg-background/85 backdrop-blur-md px-4 pt-3 pb-3">
              <div className="flex items-center gap-3">
                {/* Kept mounted (only hidden) while the recording controls are
                    shown, so the dictation session stays alive. */}
                <div className={cn("shrink-0", isDictating && "hidden")}>
                  <SpeechInput
                    ref={speechInputRef}
                    variant="outline"
                    size="icon"
                    className="h-12 w-12 rounded-full border-2"
                    onTranscript={handleTranscript}
                    language={speechLanguage}
                    onPermissionDenied={handlePermissionDenied}
                    onError={handleError}
                    onConnectionLost={handleConnectionLost}
                    onRecordingChange={handleRecordingChange}
                    onLiveTranscriptChange={setIsLiveTranscript}
                    onStreamChange={setRecordingStream}
                    showInlineVisualizer={false}
                  />
                </div>
                {isDictating ? (
                  <VoiceRecordingBar
                    variant="inline"
                    fluid
                    isLiveTranscript={isLiveTranscript}
                    stream={recordingStream}
                    onCancel={handleCancelRecording}
                    onConfirm={handleConfirmRecording}
                  />
                ) : (
                  <ButtonLoading
                    isLoading={isLoading}
                    type="submit"
                    className="flex-1 h-12 rounded-full text-base font-semibold shadow-sm transition-[flex-basis,width] duration-300 ease-out active:brightness-90"
                  >
                    {t("company.notes.save")}
                  </ButtonLoading>
                )}
              </div>
            </div>
          </form>
        </MobileSheet>
      </>
    );
  }

  return (
    <>
      {trigger}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-[800px] w-full">
          <DialogHeader>
            <div className="flex flex-row gap-2">
              <div className="flex flex-col gap-2">
                <DialogTitle>{t("company.notes.title")}</DialogTitle>
                {lastModifiedLabel && (
                  <DialogDescription>
                    <div className="text-sm text-gray-400">
                      {lastModifiedLabel}
                    </div>
                  </DialogDescription>
                )}
              </div>
              <div className="flex flex-row justify-end items-center ml-auto">
                <Tooltip delayDuration={0}>
                  <TooltipTrigger>
                    <Badge variant="outline" className="p-2">
                      <StarsIcon className="mr-2 w-4 h-4" />
                      {t("company.notes.aiEnrichmentBadge")}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: t("company.notes.aiEnrichmentTooltip"),
                      }}
                    />
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </DialogHeader>
          <form onSubmit={updateNotes}>
            <Separator className="mb-4" />
            <div className="relative">
              <TextareaAutosize
                ref={noteTextareaRef}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <div className="absolute right-2 bottom-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* Kept mounted (only hidden) while the recording controls
                        are shown, so the dictation session stays alive. */}
                    <div className={cn(isDictating && "hidden")}>
                      <SpeechInput
                        ref={speechInputRef}
                        variant="ghost"
                        size="icon"
                        onTranscript={handleTranscript}
                        language={speechLanguage}
                        onPermissionDenied={handlePermissionDenied}
                        onError={handleError}
                        onConnectionLost={handleConnectionLost}
                        onRecordingChange={handleRecordingChange}
                        onLiveTranscriptChange={setIsLiveTranscript}
                        onStreamChange={setRecordingStream}
                        showInlineVisualizer={false}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {t("chat.input.speech.tooltip")}
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <Separator className="my-4" />
            {/* The textarea above stays visible while dictating — it is the only
                feedback that the transcript is coming — so only the footer swaps
                to the recording controls, exactly as the chat composer does. */}
            <DialogFooter>
              {isDictating ? (
                // Saving mid-dictation would close the dialog and take the
                // recording down with it, so the footer belongs to the
                // recording controls until the user confirms or discards.
                <VoiceRecordingBar
                  variant="inline"
                  isLiveTranscript={isLiveTranscript}
                  stream={recordingStream}
                  onCancel={handleCancelRecording}
                  onConfirm={handleConfirmRecording}
                />
              ) : (
                <>
                  <DialogClose asChild>
                    <Button variant="ghost">{t("company.notes.close")}</Button>
                  </DialogClose>
                  <ButtonLoading isLoading={isLoading} type="submit">
                    {t("company.notes.save")}
                  </ButtonLoading>
                </>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
